import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle } from 'lucide-react'
import ActionCell from './components/ActionCell'
import {
  getDeviceConnectionState,
  getConnectionDisplay,
  formatLastConnectedAt,
} from '@/utils/deviceConnectionStatus'

export interface DeviceData {
  broadcast_device_id: number
  name?: string
  broadcast_type: string
  is_active: boolean
  clinic?: {
    clinic_id: number
    clinic_name: string
    tags?: string[]
  }
  app_version?: string
  /** 最後連線時間（從 DB 取得） */
  last_connected_at?: string | null
  /** 最後播放時間（從 DB 取得） */
  last_played_at?: string | null
  created_at?: string
  updated_at?: string
}

export const getColumns = (): ColumnDef<DeviceData>[] => [
  {
    accessorKey: 'broadcast_device_id',
    header: '裝置 ID',
    cell: ({ row }) => <Badge variant="outline">{row.getValue('broadcast_device_id')}</Badge>,
  },
  {
    accessorKey: 'name',
    header: '裝置名稱',
    cell: ({ row }) => row.getValue('name') || '-',
  },
  {
    accessorKey: 'clinic.clinic_name',
    header: '所屬診所',
    cell: ({ row }) => {
      const clinic = row.original.clinic
      if (!clinic) return '-'
      const tags = clinic.tags ?? []
      return (
        <div className="flex flex-col gap-1">
          <span>{clinic.clinic_name}</span>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'broadcast_type',
    header: '裝置類型',
    cell: ({ row }) => {
      const type = row.getValue('broadcast_type') as string
      return (
        <Badge variant="secondary">
          {type === 'LIVE' ? '即時播放' : type === 'VOD' ? '影片播放' : type}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'app_version',
    header: '版本',
    cell: ({ row }) => {
      const version = row.getValue('app_version') as string | undefined
      if (!version) return <span className="text-muted-foreground">未知</span>
      return <span className="font-mono text-sm">{version}</span>
    },
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => {
      const isActive = row.getValue('is_active') as boolean
      return (
        <Badge variant={isActive ? 'default' : 'secondary'}>{isActive ? '啟用' : '停用'}</Badge>
      )
    },
  },
  {
    accessorKey: 'last_connected_at',
    header: '最後連線',
    cell: ({ row, table }) => {
      const deviceId = row.original.broadcast_device_id
      const meta = table.options.meta as
        | {
            onlineDeviceIds?: number[]
            deviceLastConnectedMap?: Record<number, string | null>
          }
        | undefined

      // 從 WebSocket 取得即時在線狀態，優先使用 WebSocket 的 lastConnectedAt
      const isOnline = meta?.onlineDeviceIds?.includes(deviceId) ?? false
      const lastConnectedAt =
        meta?.deviceLastConnectedMap?.[deviceId] ??
        (row.getValue('last_connected_at') as string | null | undefined)

      const { state, lastConnectedAt: normalizedTime } = getDeviceConnectionState(
        isOnline,
        lastConnectedAt,
      )
      const display = getConnectionDisplay(state)

      if (state === 'connected') {
        return (
          <div className="flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-full ${display.dotClass}`} />
            <span className={display.textClass}>{display.text}</span>
          </div>
        )
      }

      if (!normalizedTime) {
        return <span className="text-muted-foreground">從未連線</span>
      }

      return (
        <div className="flex items-center gap-1">
          <span className={`inline-block w-2 h-2 rounded-full ${display.dotClass}`} />
          <span className={display.textClass}>{formatLastConnectedAt(normalizedTime)}</span>
          {display.showWarning && <AlertTriangle className="h-3 w-3 text-orange-500" />}
        </div>
      )
    },
  },
  {
    accessorKey: 'last_played_at',
    header: '最後播放',
    cell: ({ row }) => {
      const lastPlayedAt = row.original.last_played_at
      if (!lastPlayedAt) {
        return <span className="text-muted-foreground">從未播放</span>
      }
      return (
        <span className="text-sm">
          {formatLastConnectedAt(lastPlayedAt)}
        </span>
      )
    },
  },
  {
    id: 'actions',
    header: '操作',
    cell: ({ row }) => <ActionCell device={row.original} />,
  },
]
